import { useEffect, useState } from 'react'
import { api } from '../api/client'
import { fmtDate } from '../lib/format'
import Badge from '../components/Badge'
import type { Project } from '../types'

type Status = Awaited<ReturnType<typeof api.integrations.status>>

export default function SettingsTab({
  project,
  onProjectUpdated,
}: {
  project?: Project
  onProjectUpdated?: (p: Project) => void
}) {
  const [status, setStatus] = useState<Status | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [msg, setMsg] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [projDraft, setProjDraft] = useState<Partial<Project>>({})
  const [projSaving, setProjSaving] = useState(false)

  async function refresh() {
    try {
      const s = await api.integrations.status()
      setStatus(s)
    } catch (e) {
      setErr((e as Error).message)
    }
  }

  useEffect(() => {
    void refresh()
    // If we just came back from an OAuth callback, the URL has e.g. ?tab=settings&qbo=connected
    const url = new URL(window.location.href)
    if (url.searchParams.get('qbo') === 'connected') setMsg('QuickBooks Online connected.')
    if (url.searchParams.get('gmail') === 'connected') setMsg('Gmail connected.')
    if (url.searchParams.has('qbo') || url.searchParams.has('gmail')) {
      url.searchParams.delete('qbo')
      url.searchParams.delete('gmail')
      window.history.replaceState({}, '', url.toString())
    }
  }, [])

  async function qboSync() {
    setBusy('qbo-sync')
    setErr(null)
    setMsg(null)
    try {
      const r = await api.integrations.qboSync()
      setMsg(`Pulled ${r.fetched} QBO transactions since ${r.since}.`)
      await refresh()
    } catch (e) {
      setErr((e as Error).message)
    } finally {
      setBusy(null)
    }
  }

  async function disconnect(provider: 'qbo' | 'gmail') {
    if (!window.confirm(`Disconnect ${provider.toUpperCase()}? You'll need to re-authorize next time.`)) return
    setBusy(`${provider}-disconnect`)
    try {
      if (provider === 'qbo') await api.integrations.qboDisconnect()
      else await api.integrations.gmailDisconnect()
      await refresh()
      setMsg(`${provider.toUpperCase()} disconnected.`)
    } finally {
      setBusy(null)
    }
  }

  if (!status)
    return (
      <section>
        <h2>Settings</h2>
        <p className="muted">Loading…</p>
      </section>
    )

  return (
    <section>
      <h2>Settings</h2>
      <p className="muted small mb-3">Connect external services. Per integration: setup needs both Worker secrets (Dustin sets) AND clicking Connect (signs in to the service).</p>

      {msg && <div className="callout mb-3" style={{ borderLeftColor: 'var(--ok)' }}>{msg}</div>}
      {err && <div className="card alert mb-3">{err}</div>}

      {/* Active project — editable subset of fields. Always available; mirrors
          the alert click-through path on the Today tab so start_date can be
          set from either surface. */}
      {project && (
        <div className="card mb-3">
          <h3>Active project · {project.name}</h3>
          <p className="muted small mb-2">
            Status: <strong>{project.status}</strong>. Address {project.address ?? '—'}.
          </p>
          <div className="form-grid-2">
            <label>
              <span className="form-label">Target ground-break</span>
              <input
                type="date"
                value={
                  (projDraft.target_groundbreak_date as string | undefined) ??
                  project.target_groundbreak_date ??
                  ''
                }
                onChange={(e) =>
                  setProjDraft({ ...projDraft, target_groundbreak_date: e.target.value })
                }
              />
            </label>
            <label>
              <span className="form-label">Actual start date</span>
              <input
                type="date"
                value={(projDraft.start_date as string | undefined) ?? project.start_date ?? ''}
                onChange={(e) => setProjDraft({ ...projDraft, start_date: e.target.value })}
              />
            </label>
          </div>
          <div className="row gap-2 mt-2">
            <button
              className="btn-primary small"
              disabled={projSaving || Object.keys(projDraft).length === 0}
              onClick={async () => {
                setProjSaving(true)
                setErr(null)
                try {
                  // If start_date is being set for the first time and project
                  // is still in planning/pre-construction, advance to active.
                  const body: Partial<Project> = { ...projDraft }
                  if (
                    body.start_date &&
                    !project.start_date &&
                    project.status !== 'active'
                  ) {
                    body.status = 'active'
                  }
                  const updated = await api.projects.update(project.id, body)
                  onProjectUpdated?.(updated)
                  setProjDraft({})
                  setMsg('Project saved.')
                } catch (e) {
                  setErr((e as Error).message)
                } finally {
                  setProjSaving(false)
                }
              }}
            >
              {projSaving ? 'Saving…' : 'Save project'}
            </button>
            {Object.keys(projDraft).length > 0 && (
              <button className="btn-ghost small" onClick={() => setProjDraft({})}>
                Discard
              </button>
            )}
          </div>
        </div>
      )}

      {/* QuickBooks Online */}
      <div className="card mb-3">
        <div className="row-between mb-2">
          <h3>QuickBooks Online</h3>
          <div className="row gap-2">
            {!status.qbo.configured ? (
              <Badge color="gray">SECRETS NOT SET</Badge>
            ) : status.qbo.connected ? (
              <Badge color="green">CONNECTED</Badge>
            ) : (
              <Badge color="gold">READY TO CONNECT</Badge>
            )}
            <Badge color={status.qbo.env === 'production' ? 'green' : 'gray'}>{status.qbo.env.toUpperCase()}</Badge>
          </div>
        </div>

        {!status.qbo.configured && (
          <div className="callout small mb-2">
            Worker secrets <code>QBO_CLIENT_ID</code>, <code>QBO_CLIENT_SECRET</code>, <code>QBO_REDIRECT_URI</code>{' '}
            (and optionally <code>QBO_ENV=production</code>) are not set yet. See{' '}
            <strong>docs/PHASE_4_INTEGRATIONS.md</strong> in the repo for the dashboard walkthrough at developer.intuit.com.
          </div>
        )}

        {status.qbo.connected ? (
          <>
            <ul className="kv">
              <li><span>Realm (Company) ID</span><code>{status.qbo.realm_id}</code></li>
              <li><span>Connected at</span><code>{status.qbo.connected_at ? fmtDate(status.qbo.connected_at) : '—'}</code></li>
              <li><span>Last synced</span><code>{status.qbo.last_synced_at ? fmtDate(status.qbo.last_synced_at) : 'never'}</code></li>
            </ul>
            {status.qbo.last_error && <div className="card alert mt-2">{status.qbo.last_error}</div>}
            <div className="row gap-2 mt-3">
              <button className="btn-primary" onClick={qboSync} disabled={busy === 'qbo-sync'}>
                {busy === 'qbo-sync' ? 'Syncing…' : 'Sync now (last 90 days)'}
              </button>
              <button className="btn-ghost" onClick={() => disconnect('qbo')} disabled={busy === 'qbo-disconnect'}>
                Disconnect
              </button>
            </div>
          </>
        ) : (
          <div className="row gap-2 mt-2">
            <a className="btn-primary" href={api.integrations.qboConnectUrl()} aria-disabled={!status.qbo.configured} style={{ opacity: status.qbo.configured ? 1 : 0.4, pointerEvents: status.qbo.configured ? 'auto' : 'none' }}>
              Connect QuickBooks
            </a>
          </div>
        )}
      </div>

      {/* Gmail */}
      <div className="card mb-3">
        <div className="row-between mb-2">
          <h3>Gmail (send)</h3>
          {!status.gmail.configured ? (
            <Badge color="gray">SECRETS NOT SET</Badge>
          ) : status.gmail.connected ? (
            <Badge color="green">CONNECTED</Badge>
          ) : (
            <Badge color="gold">READY TO CONNECT</Badge>
          )}
        </div>

        {!status.gmail.configured && (
          <div className="callout small mb-2">
            Worker secrets <code>GOOGLE_CLIENT_ID</code>, <code>GOOGLE_CLIENT_SECRET</code>,{' '}
            <code>GOOGLE_REDIRECT_URI</code> not set. See <strong>docs/PHASE_4_INTEGRATIONS.md</strong> for the
            walkthrough at console.cloud.google.com.
          </div>
        )}

        {status.gmail.connected ? (
          <>
            <ul className="kv">
              <li><span>Account</span><code>{status.gmail.account_email}</code></li>
              <li><span>Connected at</span><code>{status.gmail.connected_at ? fmtDate(status.gmail.connected_at) : '—'}</code></li>
            </ul>
            {status.gmail.last_error && <div className="card alert mt-2">{status.gmail.last_error}</div>}
            <div className="row gap-2 mt-3">
              <button className="btn-ghost" onClick={() => disconnect('gmail')} disabled={busy === 'gmail-disconnect'}>
                Disconnect
              </button>
            </div>
          </>
        ) : (
          <div className="row gap-2 mt-2">
            <a className="btn-primary" href={api.integrations.gmailConnectUrl()} aria-disabled={!status.gmail.configured} style={{ opacity: status.gmail.configured ? 1 : 0.4, pointerEvents: status.gmail.configured ? 'auto' : 'none' }}>
              Connect Gmail
            </a>
          </div>
        )}
      </div>

      <div className="callout small">
        <strong>Why both connections need OAuth + secrets:</strong> Cloudflare Worker secrets identify <em>Heritage's app</em> to the
        provider. The Connect button signs <em>you</em> in so we get a token to call the API on your behalf. Both must be in
        place before the integration works.
      </div>

      {/* v2 utilities */}
      <V2Utilities />
    </section>
  )
}

function V2Utilities() {
  const [taxYear, setTaxYear] = useState<number>(new Date().getFullYear())
  const [projects, setProjects] = useState<Array<{ id: string; name: string; latitude: number | null; longitude: number | null }>>([])
  const [busy, setBusy] = useState<string | null>(null)
  const [msg, setMsg] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => {
    api.projects
      .list()
      .then((list) =>
        setProjects(list.map((p) => ({ id: p.id, name: p.name, latitude: p.latitude, longitude: p.longitude })))
      )
      .catch(() => undefined)
  }, [])

  function downloadTaxCsv() {
    window.open(`/api/tax/package.csv?year=${taxYear}`, '_blank')
  }
  async function geocode(projectId: string) {
    setBusy(projectId)
    setErr(null)
    setMsg(null)
    try {
      const r = await api.weather.geocode(projectId)
      setMsg(`Geocoded to ${r.matched_address} · grid ${r.grid.id}/${r.grid.x},${r.grid.y}`)
      const list = await api.projects.list()
      setProjects(list.map((p) => ({ id: p.id, name: p.name, latitude: p.latitude, longitude: p.longitude })))
    } catch (e) {
      setErr((e as Error).message)
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="card mt-3">
      <h3>v2 utilities</h3>
      {msg && <p className="muted small"><strong>OK.</strong> {msg}</p>}
      {err && <div className="card alert">{err}</div>}

      <div className="mt-2">
        <strong>Tax package (Module 14)</strong>
        <p className="muted small">CPA-ready CSV: construction costs by SCS line, operating expenses by COA, draws received, GC fees, K-1 split.</p>
        <div className="row gap-2">
          <input
            type="number"
            value={taxYear}
            onChange={(e) => setTaxYear(parseInt(e.target.value, 10) || taxYear)}
            style={{ width: 100 }}
          />
          <button className="btn-primary small" onClick={downloadTaxCsv}>
            Download CSV
          </button>
          <a
            className="btn-ghost small"
            href={`/api/tax/package?year=${taxYear}`}
            target="_blank"
            rel="noreferrer"
          >
            View JSON
          </a>
        </div>
      </div>

      <div className="mt-3">
        <strong>Weather geocoding (Module 6)</strong>
        <p className="muted small">Each project needs lat/lon before its 7-day forecast is pulled. Census Bureau geocoder runs free.</p>
        <ul style={{ listStyle: 'none', padding: 0 }}>
          {projects.map((p) => (
            <li key={p.id} className="row mt-1" style={{ alignItems: 'center', gap: '0.5rem' }}>
              <strong>{p.name}</strong>
              {p.latitude && p.longitude ? (
                <span className="muted small">
                  · {p.latitude.toFixed(4)}, {p.longitude.toFixed(4)}
                </span>
              ) : (
                <span className="muted small">· not geocoded</span>
              )}
              <button
                className="btn-ghost small"
                onClick={() => geocode(p.id)}
                disabled={busy === p.id}
                style={{ marginLeft: 'auto' }}
              >
                {busy === p.id ? 'Geocoding…' : p.latitude ? 'Re-geocode' : 'Geocode'}
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}
